import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import {
	InspectionTemplate,
	InspectionTemplateDraft,
	InspectionTemplateSection,
	TEMPLATE_INITIAL_VALUES,
} from "@/shared/models/Inspection";
import {
	confirmInspectionTemplate,
	deleteInspectionTemplate,
	unscheduleInspectionTemplate,
} from "@/shared/repositories/InspectionTemplateRepository";
import {
	ErrorModal,
	useContextMenuMESF,
	useHasPermission,
} from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	CellClickedEvent,
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useInspectionTemplateCatalog } from "../hooks/useInspectionTemplateCatalog";
import { useTableData } from "../hooks/useTableData";
import { useTemplateOptionsFunctions } from "../hooks/useTemplateOptionsFunctions";
import {
	asDraft,
	asDuplicateDraft,
	asFirstRevisionDraft,
	asRevisionDraft,
	sectionRevisions,
} from "../utils/templateDrafts";
import { TemplateModalMode, TemplateModalTarget } from "../utils/templateModal";
import { TemplateRevisionsPanel } from "./TemplateRevisionsPanel";
import { UpsertDeleteTemplateModal } from "./UpsertDeleteTemplateModal";
import { VERSION_STATUS } from "@/pages/configuration/versions/versionStatus";
import { ConfirmVersionModal } from "@/pages/configuration/versions/ConfirmVersionModal";

type RevisionAction = {
	kind: "changeToDraft" | "delete";
	template: InspectionTemplate;
};

export const TableInspectionTemplates = () => {
	const queryClient = useQueryClient();
	const canEditPublished = useHasPermission()(
		"ui.configuration.inspection.templates.edit-published",
	);

	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [target, setTarget] = useState<TemplateModalTarget | null>(null);
	const [confirmTarget, setConfirmTarget] = useState<InspectionTemplate | null>(
		null,
	);
	const [action, setAction] = useState<RevisionAction | null>(null);
	const [error, setError] = useState("");

	const { data, isLoading } = useInspectionTemplateCatalog((queryError) =>
		setError(queryError.message),
	);

	const templates = data?.templates ?? [];
	const columns = data?.columns ?? [];
	const sections = data?.sections ?? [];

	const open = (
		mode: TemplateModalMode,
		draft: InspectionTemplateDraft,
		sectionName: string,
	) =>
		setTarget({
			openedAt: Date.now(),
			mode,
			draft,
			sectionName,
		});

	const hasPendingRevision = (sectionId: number) =>
		sectionRevisions(templates, sectionId).pending !== null;

	const newTemplate = () => {
		gridApi?.deselectAll();
		open("create", TEMPLATE_INITIAL_VALUES, "");
	};

	const newRevisionFor = (section: InspectionTemplateSection) => {
		const revisions = sectionRevisions(templates, section.SectionId);
		const base = revisions.current ?? revisions.latest;

		if (revisions.pending !== null) return;

		open(
			"revision",
			base === null
				? asFirstRevisionDraft(section.SectionId)
				: asRevisionDraft(columns, base),
			section.SectionName,
		);
	};

	const duplicateForSection = (template: InspectionTemplate) =>
		open("duplicate", asDuplicateDraft(columns, template), "");

	const editDraft = (template: InspectionTemplate) =>
		open("update", asDraft(template, columns), template.SectionName);

	const editPublished = (template: InspectionTemplate) =>
		open("correct", asDraft(template, columns), template.SectionName);

	const viewTemplate = (template: InspectionTemplate) =>
		open("view", asDraft(template, columns), template.SectionName);

	const toggleDetail = (
		params: CellClickedEvent<InspectionTemplateSection>,
	) => {
		if (params.node.detail) return;
		if (params.colDef.colId === "actions") return;
		const target = params.event?.target as HTMLElement | null;
		if (target?.closest(".ag-group-expanded, .ag-group-contracted")) return;

		params.node.setExpanded(!params.node.expanded);
	};

	const openCurrent = (
		params: RowDoubleClickedEvent<InspectionTemplateSection>,
	) => {
		const revisions = sectionRevisions(
			templates,
			params.data?.SectionId ?? null,
		);
		const template = revisions.current ?? revisions.latest;
		if (template === null) return;

		if (template.StatusCode === VERSION_STATUS.DRAFT) editDraft(template);
		else viewTemplate(template);
	};

	const refresh = () =>
		queryClient.invalidateQueries({ queryKey: ["inspection-templates"] });

	const confirmMutation = useMutation(confirmInspectionTemplate, {
		meta: { successMessage: "Specification confirmed" },
		onSuccess: async () => {
			await refresh();
			setConfirmTarget(null);
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const unscheduleMutation = useMutation(unscheduleInspectionTemplate, {
		meta: { successMessage: "Revision changed to Draft" },
		onSuccess: async () => {
			await refresh();
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const deleteMutation = useMutation(deleteInspectionTemplate, {
		meta: { successMessage: "Draft deleted" },
		onSuccess: async () => {
			await refresh();
		},
		onError: (mutationError: Error) => setError(mutationError.message),
	});

	const handleActionClose = (value?: string) => {
		const pending = action;
		setAction(null);
		if (value !== "OK" || pending?.template.TemplateId == null) return;
		if (pending.kind === "changeToDraft")
			unscheduleMutation.mutate(pending.template.TemplateId);
		if (pending.kind === "delete")
			deleteMutation.mutate(pending.template.TemplateId);
	};

	const confirmRevisions = sectionRevisions(
		templates,
		confirmTarget?.SectionId ?? null,
	);

	const fallsBackTo =
		action === null
			? null
			: (sectionRevisions(templates, action.template.SectionId)
					.all.filter(
						(template) =>
							template.IsActive &&
							template.IsConfirmed &&
							template.TemplateId !== action.template.TemplateId,
					)
					.sort(
						(a, b) =>
							(b.EffectiveFrom?.getTime() ?? 0) -
							(a.EffectiveFrom?.getTime() ?? 0),
					)[0] ?? null);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useTemplateOptionsFunctions({
		newRevisionFor,
		hasPendingRevision,
	});
	const { columnDefs } = useTableData({
		newRevisionFor,
		hasPendingRevision,
		showContextMenu,
	});
	const { defaultColDef } = useGridDefinitions({});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "inspectionTemplateContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[sections],
	);

	useEffect(() => {
		registerConfig({
			id: "inspectionTemplateContext",
			getOptions: getMenuOptions,
		});
	}, [data]);

	return (
		<Grid2 container justifyContent="center" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Inspection Templates
				</Typography>
				<Typography variant="body2" color="textSecondary">
					The sheet each section is inspected with on this mill. Open a section
					to read the revisions it has spent; a draft is written first and takes
					over once it is confirmed, on its effective date.
				</Typography>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 12 }}>
				<TextFieldSearchFilter
					value={filterValue}
					onChange={(value) => {
						setFilterValue(value);
						gridApi?.setGridOption("quickFilterText", value);
					}}
				/>
			</Grid2>
			<Grid2 container justifyContent="center" alignItems="center">
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} sx={{ height: "70vh", width: "100%" }}>
						<AgGridReact<InspectionTemplateSection>
							loading={isLoading}
							rowData={sections}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							masterDetail
							detailCellRenderer={TemplateRevisionsPanel}
							detailCellRendererParams={{
								revisions: templates,
								onEdit: editDraft,
								onView: viewTemplate,
								onDuplicate: duplicateForSection,
								onConfirm: setConfirmTarget,
								onChangeToDraft: (template: InspectionTemplate) =>
									setAction({ kind: "changeToDraft", template }),
								onDelete: (template: InspectionTemplate) =>
									setAction({ kind: "delete", template }),
								onEditPublished: editPublished,
								canEditPublished,
							}}
							detailRowAutoHeight
							onCellClicked={toggleDetail}
							onRowDoubleClicked={openCurrent}
							getContextMenuItems={getContextMenuItems}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) => `${params.data.SectionId}`}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="TEMPLATE"
					dataTestId="new-template-button"
					showCreateModal={target?.mode === "create"}
					setShowCreateModal={(show) =>
						show ? newTemplate() : setTarget(null)
					}
					sizes={{ md: 3 }}
					legend={
						<Grid2 size={{ xs: 12, md: 8 }}>
							<Typography variant="body2" color="textSecondary">
								<strong>Effective date</strong> — the day a revision takes over;
								inspections already made keep theirs.
							</Typography>
						</Grid2>
					}
				/>
			</Grid2>
			<UpsertDeleteTemplateModal
				target={target}
				onHide={() => setTarget(null)}
			/>
			<ConfirmVersionModal
				open={confirmTarget !== null}
				maxRev={confirmRevisions.highest}
				lastEffectiveDate={confirmRevisions.latest?.EffectiveFrom ?? null}
				lastUsedDate={confirmTarget?.LastInspectedAt ?? null}
				allowPastDates
				isSubmitting={confirmMutation.isPending}
				onClose={() => setConfirmTarget(null)}
				onSubmit={({ rev, effectiveDate }) => {
					if (confirmTarget?.TemplateId == null) return;
					confirmMutation.mutate({
						templateId: confirmTarget.TemplateId,
						rev,
						effectiveFrom: effectiveDate,
					});
				}}
			/>
			<ConfirmationDialogRaw
				open={action !== null}
				title={
					action?.kind === "changeToDraft"
						? "Change Revision to Draft"
						: "Delete Draft"
				}
				message={
					action?.kind === "changeToDraft"
						? action.template.StatusCode === VERSION_STATUS.SCHEDULED
							? "Change scheduled revision to Draft? Its saved columns and drawing will be preserved. It will no longer become effective automatically."
							: `Change Rev ${action.template.Rev} to Draft? Its saved columns and drawing will be preserved. ${
									fallsBackTo === null
										? "The section is left with no revision in force until it is confirmed again."
										: `The section goes back to Rev ${fallsBackTo.Rev} until it is confirmed again.`
								}`
						: "Delete and discard this draft? Confirmed revisions are unaffected."
				}
				buttons={{
					no: "Cancel",
					yes:
						action?.kind === "changeToDraft" ? "Back to Draft" : "Delete Draft",
				}}
				onClose={handleActionClose}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
